import React, { useEffect, useState } from 'react';
import { Modal, Pressable, PressableProps, useWindowDimensions } from 'react-native';
import { DNABox, DNABoxProps, DNAButton, DNAIcon, DNAText } from '@alucio/lux-ui';
import { useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import AISearchHistory from 'src/screens/Debug/Tabs/AISearchHistory';

const PressableDNABox = DNABox as React.ComponentType<DNABoxProps & PressableProps>;

export const AISearchTimer: React.FC = () => {
  const { aiSearchSessionData: { loading, request } } = useDocumentSearchV2Context();
  const startedAt = request?.startedAt;
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!loading || startedAt === undefined) return;
    const update = () => setSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [loading, startedAt]);

  if (!loading || startedAt === undefined) return null;
  return <DNAText accessibilityLabel={`Search elapsed time: ${seconds} seconds`}>{`${seconds}s`}</DNAText>;
};

const HistoryControls: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { width } = useWindowDimensions();
  return (
    <DNABox appearance="row" alignY="center" spacing="xs" style={{ marginLeft: 6 }}>
      <AISearchTimer />
      <DNAButton
        appearance="ghost"
        status="subtle"
        size="sm"
        iconLeft="caret-left"
        accessibilityLabel="Open AI Search history"
        onPress={() => setOpen(true)}
      />
      {open && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setOpen(false)}>
          <DNABox appearance="row" style={{ flex: 1 }}>
            <PressableDNABox
              as={Pressable}
              accessibilityRole="button"
              accessibilityLabel="Close AI Search history backdrop"
              onPress={() => setOpen(false)}
              style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.3)' }}
            />
            <DNABox
              appearance="col"
              style={{ marginLeft: 'auto', width: Math.min(760, width), height: '100%', backgroundColor: 'white' }}
            >
              <DNABox appearance="row" alignY="center" spacing="between" style={{ padding: 16 }}>
                <DNAIcon.Styled name="clock-counter-clockwise" size="md" status="subtle" />
                <DNAButton
                  appearance="ghost"
                  status="subtle"
                  iconLeft="x"
                  accessibilityLabel="Close AI Search history"
                  onPress={() => setOpen(false)}
                />
              </DNABox>
              <DNABox style={{ flex: 1, minHeight: 0 }}>
                <AISearchHistory compact />
              </DNABox>
            </DNABox>
          </DNABox>
        </Modal>
      )}
    </DNABox>
  );
};

const AISearchHistoryControls: React.FC = () => {
  const enabled = useFeatureFlags('enableAISearchFlowSelector');
  const { isAiSearch } = useDocumentSearchV2Context();
  return enabled && isAiSearch ? <HistoryControls /> : null;
};

export default AISearchHistoryControls;
